import { Box, Group, Stack, Text, Title } from "@mantine/core";
import {
	IconChartBar,
	IconMapPin,
	IconShieldCheck,
	IconUsers,
} from "@tabler/icons-react";

const FEATURES = [
	{
		icon: IconChartBar,
		title: "Data Real-Time",
		desc: "APBDes, kinerja divisi, dan laporan tersedia kapan saja",
		accent: "#60A5FA",
	},
	{
		icon: IconUsers,
		title: "Layanan Publik Terintegrasi",
		desc: "Pengaduan warga dan surat keterangan dalam satu platform",
		accent: "#34D399",
	},
	{
		icon: IconShieldCheck,
		title: "Keamanan Data Terjamin",
		desc: "Sistem enkripsi berlapis untuk perlindungan informasi desa",
		accent: "#FBBF3B",
	},
];

/** Panel brand kiri halaman masuk (hanya tampil di layar md ke atas). */
export function SigninBrandPanel() {
	return (
		<Box
			visibleFrom="md"
			style={{
				flex: "0 0 58%",
				position: "relative",
				background:
					"linear-gradient(155deg, #040d1a 0%, #071833 35%, #0b2246 65%, #0e2f60 100%)",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				padding: "44px 52px",
			}}
		>
			{/* Dot-grid texture */}
			<Box
				style={{
					position: "absolute",
					inset: 0,
					backgroundImage:
						"radial-gradient(circle, rgba(59,143,255,0.11) 1px, transparent 1px)",
					backgroundSize: "30px 30px",
					pointerEvents: "none",
				}}
			/>
			{/* Ambient glow top-right */}
			<Box
				style={{
					position: "absolute",
					top: "-80px",
					right: "-80px",
					width: "460px",
					height: "460px",
					background:
						"radial-gradient(circle, rgba(59,143,255,0.09) 0%, transparent 65%)",
					pointerEvents: "none",
				}}
			/>
			{/* Ambient glow bottom-left */}
			<Box
				style={{
					position: "absolute",
					bottom: "40px",
					left: "-60px",
					width: "320px",
					height: "320px",
					background:
						"radial-gradient(circle, rgba(52,211,153,0.06) 0%, transparent 70%)",
					pointerEvents: "none",
				}}
			/>

			{/* Brand mark */}
			<Box style={{ position: "relative", zIndex: 1 }}>
				<Group gap={12} align="center">
					<Box
						style={{
							width: 42,
							height: 42,
							borderRadius: 11,
							background: "rgba(59,143,255,0.14)",
							border: "1px solid rgba(59,143,255,0.28)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<img
							src="/white-1.png"
							alt="Logo Desa Plus"
							style={{ width: 26, height: 26, objectFit: "contain" }}
						/>
					</Box>
					<Box>
						<Text
							style={{
								color: "#fff",
								fontWeight: 700,
								fontSize: 15,
								lineHeight: 1.2,
							}}
						>
							Dashboard Desa Plus
						</Text>
						<Text
							style={{
								color: "rgba(255,255,255,0.4)",
								fontSize: 10,
								fontWeight: 600,
								letterSpacing: "0.1em",
								textTransform: "uppercase",
							}}
						>
							Desa Darmasaba
						</Text>
					</Box>
				</Group>
			</Box>

			{/* Hero content */}
			<Box
				style={{
					position: "relative",
					zIndex: 1,
					flex: 1,
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					maxWidth: 500,
					paddingTop: 32,
					paddingBottom: 32,
				}}
			>
				{/* Badge */}
				<Box
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 8,
						background: "rgba(59,143,255,0.1)",
						border: "1px solid rgba(59,143,255,0.22)",
						borderRadius: 999,
						padding: "5px 14px",
						marginBottom: 22,
						width: "fit-content",
					}}
				>
					<Box
						style={{
							width: 6,
							height: 6,
							borderRadius: "50%",
							background: "#60A5FA",
							flexShrink: 0,
							boxShadow: "0 0 6px #60A5FA",
						}}
					/>
					<Text
						style={{
							color: "#60A5FA",
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: "0.07em",
							textTransform: "uppercase",
						}}
					>
						Sistem Administrasi Desa Terintegrasi
					</Text>
				</Box>

				{/* Headline */}
				<Title
					order={1}
					style={{
						color: "#fff",
						fontSize: 40,
						fontWeight: 800,
						lineHeight: 1.18,
						letterSpacing: "-0.025em",
						marginBottom: 18,
					}}
				>
					Kelola Desa
					<br />
					<span style={{ color: "#3B8FFF" }}>Lebih Cerdas</span>
					<br />& Transparan
				</Title>

				{/* Sub-copy */}
				<Text
					style={{
						color: "rgba(255,255,255,0.52)",
						fontSize: 15,
						lineHeight: 1.72,
						marginBottom: 40,
						maxWidth: 420,
					}}
				>
					Platform digital untuk administrasi Desa Darmasaba. Pantau APBDes,
					kinerja divisi, data demografi, dan layanan warga dalam satu dashboard
					terpadu.
				</Text>

				{/* Feature list */}
				<Stack gap={18}>
					{FEATURES.map((f) => (
						<Group key={f.title} gap={14} align="flex-start" wrap="nowrap">
							<Box
								style={{
									width: 38,
									height: 38,
									borderRadius: 10,
									background: `${f.accent}16`,
									border: `1px solid ${f.accent}28`,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
								}}
							>
								<f.icon size={18} color={f.accent} />
							</Box>
							<Box>
								<Text
									style={{
										color: "#fff",
										fontWeight: 600,
										fontSize: 13,
										marginBottom: 2,
									}}
								>
									{f.title}
								</Text>
								<Text
									style={{
										color: "rgba(255,255,255,0.42)",
										fontSize: 12,
										lineHeight: 1.55,
									}}
								>
									{f.desc}
								</Text>
							</Box>
						</Group>
					))}
				</Stack>
			</Box>

			{/* Footer location */}
			<Box
				style={{
					position: "relative",
					zIndex: 1,
					borderTop: "1px solid rgba(255,255,255,0.07)",
					paddingTop: 20,
				}}
			>
				<Group gap={8} align="center">
					<IconMapPin size={13} color="rgba(255,255,255,0.28)" />
					<Text style={{ color: "rgba(255,255,255,0.28)", fontSize: 11 }}>
						Desa Darmasaba, Kec. Abiansemal, Kab. Badung, Bali
					</Text>
				</Group>
			</Box>
		</Box>
	);
}
